import { getI18n, useTranslation } from "react-i18next";
import { iTxt } from "@duosoftbg/nacid-components";
import React, { useEffect } from "react";
import "./stylesTableQF.css";
import { ReactComponent as TableDashedIcon } from "./icons/table-dashed.svg";
import { ReactComponent as LayoutGrid } from "./icons/layout-grid.svg";
import { ReactComponent as ButtonUp } from "./icons/ButtonUp.svg";
import { ReactComponent as ButtonDown } from "./icons/ButtonDown.svg";
import { CountryDetailsLong, CountryQF } from "../../../types/QFDataTypes";
import { eqfLevelNumberV, mainHeadersV, sideHeadersV, subHeaders1, subHeaders2 } from "./dataTableQf";
import { getColor } from "./stylesTableQfTS";

interface QFTableVerticalProps {
  countryData: CountryDetailsLong;
  handleTables: (direction: "H" | "V") => void;
  setActiveLevelV: (value: number) => void;
  setHideMainHeaderV: (value: boolean[]) => void;
  setHideSideHeaderV: (value: boolean[]) => void;
  setHideSublevelsV: (value: boolean[]) => void;
  activeLevelV: number;
  hideMainHeaderV: boolean[];
  hideSideHeaderV: boolean[];
  hideSublevelsV: boolean[];
}

export const QFTableVertical = ({
  countryData,
  handleTables,
  setActiveLevelV,
  setHideMainHeaderV,
  setHideSideHeaderV,
  setHideSublevelsV,
  activeLevelV,
  hideMainHeaderV,
  hideSideHeaderV,
  hideSublevelsV,
}: QFTableVerticalProps) => {
  const { t } = useTranslation();

  const handleActiveLevelV = (index: number) => {
    setActiveLevelV(index);
  };

  const handleMainHeaderV = (index: number) => {
    //@ts-ignore
    setHideMainHeaderV((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };

  const handleSideHeaderV = (index: number) => {
    //@ts-ignore
    setHideSideHeaderV((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };

  const handleSublevelV = (index: number) => {
    //@ts-ignore
    setHideSublevelsV((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };
  const isWholeTableHiddenV = hideMainHeaderV.every((x) => x === true) && hideSideHeaderV.every((x) => x === true);

  const handleAllRowsV = () => {
    setHideMainHeaderV(Array(2).fill(!isWholeTableHiddenV));
    setHideSideHeaderV(Array(2).fill(!isWholeTableHiddenV));
    setHideSublevelsV(Array(countryQFMap.get(activeLevelV).length).fill(!isWholeTableHiddenV));
  };

  const countryQFMap = new Map<number, CountryQF[]>();
  eqfLevelNumberV.forEach((eqfLevel) => {
    countryQFMap.set(
      eqfLevel,
      countryData.countriesQF.filter((qf) => qf.eqfLevel.id === eqfLevel).sort((a, b) => a.level - b.level),
    );
  });

  useEffect(() => {
    setHideSublevelsV(Array(countryQFMap.get(activeLevelV).length).fill(false));
    // eslint-disable-next-line
  }, [activeLevelV]);

  return (
    <React.Fragment key="table-fragment">
      <div key="table-root" className="table fade-slide" style={{ gridTemplateColumns: "repeat(4, auto)" }}>
        {/* === 2 table-control buttons === */}
        <div key="row-controls" className="control-cell fade-slide" style={{ gridColumn: "span 4", gridRow: "span 1" }}>
          <div className="row-inner">
            <div key="btn-h" className="iconPop-V-cell">
              <TableDashedIcon onClick={() => handleTables("H")} />
            </div>
            <div key="btn-v" className="iconPush-V-cell">
              <LayoutGrid onClick={() => handleTables("V")} />
            </div>

            {/* === 3 control-push-cells === */}
            <div
              key="row-push-controls"
              className="control-cell fade-slide"
              style={{ gridColumn: "span 4", gridRow: "span 1" }}
            >
              {eqfLevelNumberV.map((eqfLevel, qfIndex) => (
                <div
                  key={`btn-push-${eqfLevel}-${qfIndex}`}
                  className={eqfLevel === activeLevelV ? "common-V-push-active-cell" : "common-V-push-cell"}
                  style={{ backgroundColor: getColor(eqfLevel) }}
                  onClick={() => handleActiveLevelV(eqfLevel)}
                >
                  <div style={{ fontWeight: "bold" }}>
                    {iTxt(
                      getI18n().language,
                      countryQFMap.get(eqfLevel)[0]?.bgLevel?.name,
                      countryQFMap.get(eqfLevel)[0]?.bgLevel?.nameEn,
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* === Whole table control button === */}
            <div key="btn-all" className="iconButtonUp-VS-cell" style={{ backgroundColor: "#fff" }}>
              {!isWholeTableHiddenV ? (
                <ButtonUp className="iconButtonUp-VV-cell" onClick={() => handleAllRowsV()} />
              ) : (
                <ButtonDown className="iconButtonUp-VV-cell" onClick={() => handleAllRowsV()} />
              )}
            </div>
          </div>
        </div>

        {/* === 1st main header === */}
        <div
          key="header-main-1"
          className="header-V-cell"
          style={{ backgroundColor: getColor(1), gridColumn: "span 4" }}
        >
          {mainHeadersV(t, countryData, false)[0]}
          {!hideMainHeaderV[0] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(0)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(0)} />
          )}
        </div>

        {/* === 1st sub headers === */}
        {!hideMainHeaderV[0] &&
          subHeaders1(t).map((subHeader, index) => (
            <div
              key={`subheader-1-${index}`}
              className="subheader-V-cell fade-slide"
              style={{
                backgroundColor: getColor(2),
                gridColumn: "span 1",
                gridRow: "span 1",
              }}
            >
              {subHeader}
            </div>
          ))}

        {/* === 1st info fields === */}
        {!hideMainHeaderV[0] &&
          countryQFMap.get(activeLevelV)?.map((qfLevel, qfIndex) => (
            <React.Fragment key={`info-${activeLevelV}-${qfIndex}`}>
              <div
                key={`cellA-${activeLevelV}-${qfIndex}`}
                className="common-V-cell fade-slide"
                style={{
                  backgroundColor: getColor(activeLevelV),
                  gridColumn: "span 1",
                  gridRow: "span 1",
                }}
              >
                <div style={{ fontWeight: 600 }}>{iTxt(getI18n().language, qfLevel.name, qfLevel.nameEn)}</div>
                <div>{iTxt(getI18n().language, qfLevel.description, qfLevel.descriptionEn)}</div>
              </div>

              <div
                key={`cellB-${activeLevelV}-${qfIndex}`}
                className="common-V-cell fade-slide"
                style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 1", gridRow: "span 1" }}
              >
                {qfLevel.nameNative}
              </div>

              <div
                key={`cellC-${activeLevelV}-${qfIndex}`}
                className="common-V-cell fade-slide"
                style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 1", gridRow: "span 1" }}
              >
                {qfLevel.credits ? `${qfLevel.credits} ${countryData.creditsLabel}` : "-"}
              </div>

              <div
                key={`cellD-${activeLevelV}-${qfIndex}`}
                className="common-V-cell fade-slide"
                style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 1", gridRow: "span 1" }}
              >
                {qfLevel.duration ? `${qfLevel.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
              </div>
            </React.Fragment>
          ))}

        {/* === 2nd main header === */}
        <div
          key="header-main-2"
          className="header-V-cell"
          style={{ backgroundColor: getColor(1), gridColumn: "span 4" }}
        >
          {mainHeadersV(t, countryData, false)[1]}
          {!hideMainHeaderV[1] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(1)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(1)} />
          )}
        </div>

        {/* === 2nd sub headers === */}
        {!hideMainHeaderV[1] &&
          countryQFMap.get(activeLevelV)?.map((qfLevel, qfIndex) => (
            <React.Fragment key={`subsection-${activeLevelV}-${qfIndex}`}>
              {countryQFMap.get(activeLevelV)?.length > 1 && (
                <div
                  key={`subheader-row-${activeLevelV}-${qfIndex}`}
                  className="subheader-V-cell"
                  style={{
                    backgroundColor: getColor(2),
                    gridColumn: "span 4",
                    gridRow: "span 1",
                  }}
                >
                  {!hideSublevelsV[qfIndex] ? (
                    <ButtonUp className="iconButtonUp-v-small-cell" onClick={() => handleSublevelV(qfIndex)} />
                  ) : (
                    <ButtonDown className="iconButtonUp-v-small-cell" onClick={() => handleSublevelV(qfIndex)} />
                  )}
                  <div
                    style={{ fontWeight: 600 }}
                  >{`\u2022 ${iTxt(getI18n().language, qfLevel.name, qfLevel.nameEn)}`}</div>
                </div>
              )}

              {!hideSublevelsV[qfIndex] &&
                subHeaders2(t).map((subHeader, sIndex) => (
                  <div
                    key={`subheader-2-${activeLevelV}-${qfIndex}-${sIndex}`}
                    className="subheader-V-cell"
                    style={{
                      backgroundColor: getColor(2),
                      gridColumn: "span 1",
                      gridRow: "span 1",
                    }}
                  >
                    {subHeader}
                  </div>
                ))}

              {!hideSublevelsV[qfIndex] &&
                qfLevel.levels.map((subLevel, lIndex) => (
                  <React.Fragment key={`sublevel-${activeLevelV}-${qfIndex}-${lIndex}`}>
                    <div
                      key={`cellA-${activeLevelV}-${qfIndex}-${lIndex}`}
                      className="common-V-cell fade-slide"
                      style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 1", gridRow: "span 1" }}
                    >
                      <div style={{ fontWeight: 400 }}>{iTxt(getI18n().language, subLevel.name, subLevel.nameEn)}</div>
                    </div>

                    <div
                      key={`cellB-${activeLevelV}-${qfIndex}-${lIndex}`}
                      className="common-V-cell fade-slide"
                      style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 1", gridRow: "span 1" }}
                    >
                      {subLevel.nameNative}
                    </div>

                    <div
                      key={`cellC-${activeLevelV}-${qfIndex}-${lIndex}`}
                      className="common-V-cell fade-slide"
                      style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 1", gridRow: "span 1" }}
                    >
                      {subLevel.credits ? `${subLevel.credits} ${countryData.creditsLabel}` : "-"}
                    </div>

                    <div
                      key={`cellD-${activeLevelV}-${qfIndex}-${lIndex}`}
                      className="common-V-cell fade-slide"
                      style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 1", gridRow: "span 1" }}
                    >
                      {subLevel.duration ? `${subLevel.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
                    </div>
                  </React.Fragment>
                ))}
            </React.Fragment>
          ))}

        {/* === EQF / Bologna sections === */}
        <div key="header-eqf" className="header-V-cell" style={{ backgroundColor: getColor(1), gridColumn: "span 4" }}>
          {sideHeadersV(t, false)[0]}
          {!hideSideHeaderV[0] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(0)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(0)} />
          )}
        </div>

        {!hideSideHeaderV[0] && (
          <div
            key={`eqf-info-${activeLevelV}`}
            className="common-V-cell fade-slide"
            style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 4", gridRow: "span 1" }}
          >
            <div style={{ fontWeight: "bold" }}>
              {iTxt(
                getI18n().language,
                countryQFMap.get(activeLevelV)[0]?.eqfLevel?.name,
                countryQFMap.get(activeLevelV)[0]?.eqfLevel?.nameEn,
              )}
            </div>
            <div>
              {iTxt(
                getI18n().language,
                countryQFMap.get(activeLevelV)[0]?.eqfLevel?.description,
                countryQFMap.get(activeLevelV)[0]?.eqfLevel?.descriptionEn,
              )}
            </div>
          </div>
        )}

        <div
          key="header-bologna"
          className="header-V-cell"
          style={{ backgroundColor: getColor(1), gridColumn: "span 4" }}
        >
          {sideHeadersV(t, false)[1]}
          {!hideSideHeaderV[1] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(1)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(1)} />
          )}
        </div>

        {!hideSideHeaderV[1] && (
          <div
            key={`bologna-info-${activeLevelV}`}
            className="common-V-cell fade-slide"
            style={{ backgroundColor: getColor(activeLevelV), gridColumn: "span 4", gridRow: "span 1" }}
          >
            <div style={{ fontWeight: "bold" }}>
              {iTxt(
                getI18n().language,
                countryQFMap.get(activeLevelV)[0]?.bolognaCycle?.name,
                countryQFMap.get(activeLevelV)[0]?.bolognaCycle?.nameEn,
              )}
            </div>
            <div>
              {iTxt(
                getI18n().language,
                countryQFMap.get(activeLevelV)[0]?.bolognaCycle?.description,
                countryQFMap.get(activeLevelV)[0]?.bolognaCycle?.descriptionEn,
              )}
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};
